import {
  DataVersion,
  SharedResourceTokenResponseDTO,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { withDataVersion } from '@hakimo-ui/hakimo/util';
import useAuthenticatedRequest from '../shared/useAuthenticatedRequest';
dayjs.extend(utc);

export type ResourceType =
  | 'alarm'
  | 'location-alarm'
  | 'live-view'
  | 'alarm-group'
  | 'scan-raw-alarm';

export function mapProductToResourceType(
  product: UnifiedMonitoringProduct
): ResourceType {
  switch (product) {
    case UnifiedMonitoringProduct.SCAN:
      return 'alarm-group';
    case UnifiedMonitoringProduct.REMOTE_GUARDING:
      return 'location-alarm';
    case UnifiedMonitoringProduct.ENTERPRISE:
      return 'location-alarm';
  }
}

export function useSharedResourceToken(
  resourceId: string,
  duration: number,
  durationUnit: string,
  resourceType: ResourceType,
  timestamp?: string,
  dataVersion?: DataVersion
) {
  const url = getSharedResourceTokenUrl(
    resourceId,
    resourceType,
    duration,
    durationUnit,
    timestamp
  );
  const request = new Request(url);
  // Mint the token against the same v2/v3 dataset the alarm was read from, so
  // the by-id lookup resolves a unified (v3) alarm. Only v3 opts in.
  withDataVersion(request, dataVersion);

  return useAuthenticatedRequest<SharedResourceTokenResponseDTO>(request, {
    responseModifier: async (response) => {
      const respJson = await response.json();
      return respJson.payload;
    },
    enabled: false,
    cacheTime: 0,
  });
}

function getSharedResourceTokenUrl(
  resourceId: string,
  resourceType: ResourceType,
  duration: number,
  durationUnit: string,
  timestamp?: string
) {
  let resourceName = '';
  let path = '';
  switch (resourceType) {
    case 'alarm':
    case 'scan-raw-alarm':
      resourceName = 'alarm';
      break;
    case 'location-alarm':
    case 'alarm-group':
      resourceName = 'location_alarm';
      break;
    case 'live-view':
      resourceName = 'live_view';
      break;
  }
  let partitionKey;
  if (timestamp) {
    const date = dayjs(timestamp).utc();
    partitionKey = date.format('YYYYMM');
  } else {
    // Use current date if no timestamp provided
    const date = dayjs().utc();
    partitionKey = date.format('YYYYMM');
  }

  path = `/v2/orm/shared/${resourceName}/token/${resourceId}?duration=${duration}&durationUnit=${durationUnit}&partitionKey=${partitionKey}`;

  if (resourceType === 'alarm-group' || resourceType === 'scan-raw-alarm') {
    path += `&product=scan`;
  }

  return path;
}

export { getSharedResourceTokenUrl };

export default useSharedResourceToken;
